import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ArrowRight } from 'lucide-react';
import { api } from './api';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';

const minPasswordLength = 8;
type Mode = 'password' | 'invitation' | 'reset' | 'forgot';
interface InvitationDetails { email: string; expiresAt: string | null }
/** The front door's answer when the person belongs to another workspace (#88): go there, or pick one. */
interface SignInResult { signedIn?: boolean; handoff?: string; choose?: Array<{ company: string; url: string }> }
/** Only hand-offs to our own hosts are followed. */
const allowedHandoff = (url: string) => {
  try {
    const target = new URL(url);
    const secure = target.protocol === 'https:' || ['localhost', '127.0.0.1'].includes(target.hostname);
    return secure && (target.hostname === window.location.hostname || target.hostname.endsWith('.meetalder.com'));
  }
  catch { return false; }
};

/**
 * Go to a sign-in destination. A hand-off to this same page differs only in its #handoff=… hash, and a hash change
 * alone does not reload, so the token would never be read: reload in that case.
 */
function go(url: string) {
  const target = new URL(url, window.location.href);
  const here = window.location;
  if (target.origin === here.origin && target.pathname === here.pathname && target.search === here.search) {
    here.hash = target.hash; here.reload();
  } else here.assign(target.href);
}

const message = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback);

/**
 * WorkOS-backed sign-in rendered entirely on our own page: password sign-in, invitation acceptance and password
 * reset. Invitation and reset tokens arrive as query parameters on /signin; on success the app reloads signed in.
 * A sign-in at the front door (app.meetalder.com) may answer with a hand-off to the person's own workspace, which
 * arrives here as #handoff=<token> and is redeemed once, or with a picker when they belong to several.
 */
export function WorkosSignIn() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const invitationToken = params.get('invitation_token')?.trim() ?? '';
  const resetToken = params.get('token')?.trim() ?? '';
  const [mode, setMode] = useState<Mode>(invitationToken ? 'invitation' : resetToken ? 'reset' : 'password');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState<NonNullable<SignInResult['choose']>>([]);

  useEffect(() => { document.title = 'Sign in to Alder'; }, []);
  const [handoff, setHandoff] = useState<{ token: string; email: string; company: string } | null>(null);
  useEffect(() => {
    const match = /^#handoff=([A-Za-z0-9_-]{1,8192})$/.exec(window.location.hash);
    if (!match) return;
    // The token is single-use: take it out of the address bar and history, then show whose sign-in it is.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    const token = match[1]!;
    api<{ email: string; company: string }>('/auth/front-door/handoff', { token })
      .then(details => setHandoff({ token, ...details }))
      .catch((cause: unknown) => setError(message(cause, 'This sign-in link has expired. Sign in again.')));
  }, []);
  async function continueHandoff() {
    if (!handoff || busy) return;
    setBusy(true); setError('');
    try { await api('/auth/front-door/handoff', { token: handoff.token, confirm: true }); window.location.replace('/#home'); window.location.reload(); }
    catch (cause) { setHandoff(null); setError(message(cause, 'This sign-in link has expired. Sign in again.')); setBusy(false); }
  }
  useEffect(() => {
    if (mode !== 'invitation' || !invitationToken) return;
    let cancelled = false;
    api<InvitationDetails>(`/auth/invitation?token=${encodeURIComponent(invitationToken)}`)
      .then(details => { if (!cancelled) { setInvitation(details); setEmail(details.email); } })
      .catch((cause: unknown) => { if (!cancelled) setError(message(cause, 'This invitation link is no longer valid.')); });
    return () => { cancelled = true; };
  }, [mode, invitationToken]);

  function switchMode(next: Mode) {
    setMode(next); setError(''); setNotice('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(''); setNotice('');
    if (mode === 'forgot') {
      setBusy(true);
      try {
        await api('/auth/password/forgot', { email: email.trim() });
        setNotice('If that email has an Alder account, a reset link is on its way.');
      } catch (cause) {
        setError(message(cause, 'Could not send the reset link. Try again.'));
      } finally { setBusy(false); }
      return;
    }
    if (mode === 'password') {
      if (!email.trim() || !password) return setError('Enter your email and password.');
    } else {
      if (password.length < minPasswordLength) return setError(`Choose a password of at least ${minPasswordLength} characters.`);
      if (password !== confirm) return setError('The passwords do not match.');
    }
    setBusy(true);
    try {
      if (mode === 'password') {
        const result = await api<SignInResult>('/auth/password', { email: email.trim(), password });
        if (result.handoff || result.choose) {
          const options = result.choose ?? [{ company: '', url: result.handoff! }];
          if (!options.every(choice => allowedHandoff(choice.url))) throw new Error('Your workspace could not be opened. Contact Alder support.');
          if (result.handoff) return go(result.handoff);
          setChoices(options); setBusy(false); return;
        }
      } else if (mode === 'invitation') await api('/auth/invitation/accept', { token: invitationToken, password });
      else await api('/auth/password/reset', { token: resetToken, password });
      window.location.assign('/');
    } catch (cause) {
      setError(message(cause, 'Sign-in failed. Try again.'));
      setBusy(false);
    }
  }

  const heading = mode === 'invitation' ? 'Welcome to Alder' : mode === 'reset' ? 'Choose a new password' : mode === 'forgot' ? 'Reset your password' : 'Sign in to Alder';
  const subtitle = mode === 'invitation'
    ? (invitation ? `Set a password for ${invitation.email} to finish setting up your account.` : 'Checking your invitation…')
    : mode === 'reset' ? 'Set a new password for your Alder account.'
      : mode === 'forgot' ? 'Enter your email and we’ll send you a reset link.'
        : 'Every follow-up prepared, every send approved by you.';

  if (handoff) return (<>
    <h2>Continue to {handoff.company}</h2>
    <p>You are signing in as <strong>{handoff.email}</strong>. If that is not you, close this page.</p>
    {error && <div className="alert error" role="alert">{error}</div>}
    <div className="signin-form">
      <Button className="signin-primary" type="button" onClick={continueHandoff} disabled={busy}>
        {busy ? 'One moment…' : `Continue as ${handoff.email}`}{!busy && <ArrowRight aria-hidden />}
      </Button>
    </div>
  </>);

  if (choices.length) return (<>
    <h2>Choose a workspace</h2>
    <p>Your account belongs to more than one workspace. Pick one; to switch later, sign out and sign in again here.</p>
    <div className="signin-form">
      {choices.map(choice => (
        <Button key={choice.url} className="signin-primary" type="button" onClick={() => go(choice.url)}>
          {choice.company}<ArrowRight aria-hidden />
        </Button>
      ))}
    </div>
    <div className="signin-links">
      <Button variant="ghost" size="sm" type="button" onClick={() => { setChoices([]); setPassword(''); }}>Back to sign in</Button>
    </div>
  </>);

  return (<>
    <h2>{heading}</h2>
    <p>{subtitle}</p>
    {error && <div className="alert error" role="alert">{error}</div>}
    {notice && <div className="alert" role="status">{notice}</div>}
    <form className="signin-form" onSubmit={submit}>
      {(mode === 'password' || mode === 'forgot') && (
        <label className="signin-field"><span>Work email</span>
          <Input type="email" value={email} onChange={event => setEmail(event.target.value)} autoComplete="username" autoFocus required maxLength={200} />
        </label>
      )}
      {mode !== 'forgot' && (
        <label className="signin-field"><span>{mode === 'password' ? 'Password' : 'New password'}</span>
          <Input type="password" value={password} onChange={event => setPassword(event.target.value)}
            autoComplete={mode === 'password' ? 'current-password' : 'new-password'} autoFocus={mode === 'invitation' || mode === 'reset'}
            required minLength={mode === 'password' ? 1 : minPasswordLength} maxLength={200} />
        </label>
      )}
      {(mode === 'invitation' || mode === 'reset') && (
        <label className="signin-field"><span>Confirm password</span>
          <Input type="password" value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" required minLength={minPasswordLength} maxLength={200} />
        </label>
      )}
      <Button className="signin-primary" type="submit" disabled={busy || (mode === 'invitation' && !invitation)}>
        {busy ? 'One moment…' : mode === 'invitation' ? 'Set password and sign in' : mode === 'reset' ? 'Save password and sign in' : mode === 'forgot' ? 'Send reset link' : 'Sign in'}
        {!busy && <ArrowRight aria-hidden />}
      </Button>
    </form>
    <div className="signin-links">
      {mode === 'password' && <Button variant="ghost" size="sm" type="button" onClick={() => switchMode('forgot')}>Forgot password?</Button>}
      {mode === 'forgot' && <Button variant="ghost" size="sm" type="button" onClick={() => switchMode('password')}>Back to sign in</Button>}
      {mode === 'invitation' && error && <Button variant="ghost" size="sm" type="button" onClick={() => switchMode('password')}>Sign in instead</Button>}
    </div>
  </>);
}
